import { dayMonth, monthNames, weekdayShort, type IsoDate } from '@kancelaria/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorText } from '../../api/client.js';
import { useData } from '../../store/data.js';
import { useMarks } from '../../store/marks.js';
import { toast } from '../../store/ui.js';
import { Field, Modal } from '../ui.js';
import { DateInput } from '../DatePicker.js';

interface Preview {
  nights: Array<{
    date: IsoDate;
    fromDay: number;
    toDay: number;
    monthFrom: 'explicit' | 'weekday' | 'order';
    weekdayMismatch: boolean;
    groups: Array<{
      markKind: string | null;
      letter: string | null;
      matched: Array<{
        raw: string;
        personId: string | null;
        name: string | null;
        ambiguous: boolean;
        fuzzy: boolean;
      }>;
    }>;
  }>;
  cells: number;
  applied: number;
}

const SAMPLE = `з 24 на 25 (пн на вт)
ЧК: Іваненко
ДК: Петренко
КБ:
Сидоренко`;

/**
 * Заповнення таблиці — лише тієї, що відкрита: у харчуванні — за шаблонами
 * харчування, у нарядах — розбором вставленого списку чергувань. Кожна
 * таблиця заповнюється своїм, тож чужий блок у вікні тільки заважав.
 * Закривається хрестиком у заголовку: окрема кнопка «Закрити» дублювала його.
 */
export function FillDialog({
  part,
  from,
  to,
  year,
  month,
  onClose,
}: {
  part: 'meals' | 'duties';
  from: IsoDate;
  to: IsoDate;
  year: number;
  month: number;
  onClose: () => void;
}): ReactNode {
  const settings = useData(s => s.session!.org.settings);
  const reload = useMarks(s => s.reload);
  /** Яка саме дія зараз виконується — щоб блимала лише її кнопка. */
  const [busy, setBusy] = useState<'templates' | 'duties' | null>(null);

  /* --------------------------------------------------- за шаблонами */

  const counted = settings.markTypes.find(t => t.counted) ?? settings.markTypes[0]!;
  const [form, setForm] = useState({ from, to, overwrite: false });

  const fillTemplates = async (): Promise<void> => {
    setBusy('templates');
    try {
      const filled = await api.post<{ applied: number }>('/api/marks/fill', {
        from: form.from,
        to: form.to,
        kind: counted.code,
        presentKind: null,
        overwrite: form.overwrite,
      });
      await reload();
      toast(`Заповнено клітинок: ${filled.applied}`, 'ok');
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setBusy(null);
    }
  };

  /* --------------------------------------------------------- наряди */

  const [text, setText] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  /*
   * Місяць для чисел без місяця. Типово — відкрита таблиця, але список часто
   * приходить наприкінці місяця вже на наступний, тож поруч є вибір із
   * сусідніх місяців. Далі числа йдуть за порядком: коли число поменшало
   * («з 30 на 1», потім «з 1 на 2») — це вже наступний місяць, а день тижня
   * в дужках уточнює місяць сам.
   */
  const [base, setBase] = useState(`${year}-${month}`);
  const baseOptions = useMemo(() => {
    const names = monthNames();
    return [-1, 0, 1].map(d => {
      const idx = year * 12 + (month - 1) + d;
      const y = Math.floor(idx / 12);
      const m = (idx % 12) + 1;
      return { key: `${y}-${m}`, label: `${names[m - 1]} ${y}` };
    });
  }, [year, month]);
  const [baseYear, baseMonth] = base.split('-').map(Number) as [number, number];

  // Текст розбирається сам, щойно його вставили: окрема кнопка тут зайва.
  useEffect(() => {
    if (!text.trim()) {
      setPreview(null);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      api
        .post<Preview>('/api/duties', { text, year: baseYear, month: baseMonth, apply: false })
        .then(r => alive && setPreview(r))
        .catch(e => alive && toast(errorText(e), 'err'));
    }, 350);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [text, baseYear, baseMonth]);

  const applyDuties = async (): Promise<void> => {
    setBusy('duties');
    try {
      const res = await api.post<Preview>('/api/duties', {
        text,
        year: baseYear,
        month: baseMonth,
        apply: true,
      });
      await reload();
      toast(`Проставлено клітинок наряду: ${res.applied}`, 'ok');
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setBusy(null);
    }
  };

  const unknown =
    preview?.nights.flatMap(n => n.groups.flatMap(g => g.matched.filter(m => !m.personId))) ?? [];
  const mismatched = preview?.nights.filter(n => n.weekdayMismatch) ?? [];
  const fuzzy =
    preview?.nights.flatMap(n => n.groups.flatMap(g => g.matched.filter(m => m.personId && m.fuzzy))) ?? [];
  /** Скільки діб у якому місяці — щоб було видно, що список зайшов у наступний. */
  const byMonth = useMemo(() => {
    const names = monthNames();
    const acc = new Map<string, number>();
    for (const n of preview?.nights ?? []) {
      const key = `${(names[Number(n.date.slice(5, 7)) - 1] ?? '').toLowerCase()} ${n.date.slice(0, 4)}`;
      acc.set(key, (acc.get(key) ?? 0) + 1);
    }
    return [...acc];
  }, [preview]);
  const openKey = `${year}-${String(month).padStart(2, '0')}`;

  /* ---------------------------------------------------------- вікно */

  return (
    <Modal
      title={part === 'meals' ? 'Заповнити харчування' : 'Заповнити наряди'}
      onClose={onClose}
      width={part === 'meals' ? 520 : 720}
    >
      {part === 'meals' ? (
        <section className="fill-part">
          <p className="sub" style={{ marginTop: 0 }}>
            За категоріями людей.
          </p>
          {/* Період одним рядком на всю ширину: початок – кінець, як і в решті вікон. */}
          <div style={{ marginTop: 12 }}>
            <Field label="Період">
              <div className="period-row">
                <DateInput value={form.from} base={form.to} onChange={d => setForm({ ...form, from: d })} />
                <span className="period-dash">–</span>
                <DateInput value={form.to} base={form.from} onChange={d => setForm({ ...form, to: d })} />
              </div>
            </Field>
          </div>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.overwrite}
              onChange={e => setForm({ ...form, overwrite: e.target.checked })}
            />
            Перезаписувати наявні позначки
          </label>
          {form.overwrite ? (
            // Шаблон пише лише в харчування: таблиці нарядів і статусу він не
            // чіпає, але перенесені звідти позначки в харчуванні затре.
            <p className="note">
              Перезапис зітре й перенесені в харчування наряди та статус — їх треба буде перенести знову.
            </p>
          ) : null}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn primary" disabled={busy !== null} onClick={() => void fillTemplates()}>
              {busy === 'templates' ? 'Заповнюю…' : 'Заповнити за категоріями'}
            </button>
          </div>
        </section>
      ) : (
        <section className="fill-part">
          <div className="field-row" style={{ marginTop: 0, marginBottom: 8 }}>
            <Field label="Числа без місяця — це">
              <select className="select" value={base} onChange={e => setBase(e.target.value)}>
                {baseOptions.map(o => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <textarea
            className="textarea"
            style={{ height: 170 }}
            placeholder={SAMPLE}
            value={text}
            onChange={e => {
              setText(e.target.value);
              setPreview(null);
            }}
          />

          {preview ? (
            preview.nights.length ? (
              <>
                {unknown.length ? (
                  <p className="error-text">
                    Не знайдено у списку:{' '}
                    {unknown.map(u => u.raw + (u.ambiguous ? ' (кілька збігів)' : '')).join(', ')}
                  </p>
                ) : null}
                {fuzzy.length ? (
                  <p className="note">
                    Підібрано за схожістю:{' '}
                    {fuzzy.map(m => `${m.raw} → ${m.name}`).join(', ')}. Перевірте, що це ті люди.
                  </p>
                ) : null}
                {mismatched.length ? (
                  <p className="error-text">
                    День тижня в дужках не сходиться з датою:{' '}
                    {mismatched.map(n => `з ${n.fromDay} на ${n.toDay}`).join(', ')}. Перевірте число або
                    місяць.
                  </p>
                ) : null}
                {preview.nights.map(n => (
                  <div
                    className={`card duty-night${n.date.slice(0, 7) !== openKey ? ' other-month' : ''}${n.weekdayMismatch ? ' mismatch' : ''}`}
                    key={n.date}
                    style={{ marginBottom: 10 }}
                  >
                    <h2>
                      {dayMonth(n.date)}, {weekdayShort(n.date)}
                      <span className="muted">, з {n.fromDay} на {n.toDay}</span>
                      {n.date.slice(0, 7) !== openKey ? (
                        <span className="pill on duty-month" title="Не той місяць, що відкритий у таблиці">
                          інший місяць
                        </span>
                      ) : null}
                    </h2>
                    {n.groups.map((g, gi) =>
                      g.markKind === null ? (
                        g.matched.length ? (
                          <p key={gi} className="sub" style={{ margin: gi ? '10px 0 0' : 0 }}>
                            Без позначки: {g.matched.map(m => m.name ?? m.raw).join(', ')}
                          </p>
                        ) : null
                      ) : (
                        <div key={gi} style={{ marginTop: gi ? 10 : 0 }}>
                          {g.markKind !== settings.duties.markKind ? (
                            <div className="note" style={{ marginBottom: 4 }}>
                              Мітка «{g.letter}»:
                            </div>
                          ) : null}
                          <div className="btn-row">
                            {g.matched.map(m => (
                              <span
                                key={m.raw}
                                className={`pill ${m.personId ? 'on' : ''}${m.fuzzy ? ' fuzzy' : ''}`}
                                title={
                                  m.personId
                                    ? m.fuzzy
                                      ? `У тексті: «${m.raw}» — підібрано за схожістю`
                                      : ''
                                    : m.ambiguous
                                      ? 'кілька збігів'
                                      : 'не знайдено'
                                }
                              >
                                {m.name ?? m.raw}
                                {m.fuzzy ? <span className="pill-note">≈ {m.raw}</span> : null}
                              </span>
                            ))}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                ))}
              </>
            ) : (
              <p className="error-text">У тексті немає рядків виду «з 24 на 25».</p>
            )
          ) : null}

          {preview?.nights.length ? (
            <p className="note">
              Розібрано діб: {preview.nights.length}
              {byMonth.length > 1 ? ` (${byMonth.map(([k, n]) => `${k}: ${n}`).join(', ')})` : ''},
              клітинок: {preview.cells}
            </p>
          ) : null}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button
              className="btn primary"
              disabled={busy !== null || !preview?.cells}
              onClick={() => void applyDuties()}
            >
              {busy === 'duties' ? 'Проставляю…' : 'Проставити наряди'}
            </button>
          </div>
        </section>
      )}
    </Modal>
  );
}
